import React, { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { RefreshCw, Video, Globe, MousePointer, Users, MoreVertical, Play, Pause, Check, X } from 'lucide-react';

// Pool type icons
const POOL_TYPE_ICONS = {
    engagement: MousePointer,
    website: Globe,
    video_viewer: Video,
    custom: Users
};

const POOL_TYPE_LABELS = {
    engagement: 'Engagers',
    website: 'Site Visitors',
    video_viewer: 'Video Viewers',
    custom: 'Custom Pool'
};

// Platform compatibility for retargeting pool types
const PLATFORM_COMPATIBILITY = {
    engagement: { meta: true, google: false, tiktok: true, linkedin: true },
    website: { meta: true, google: true, tiktok: true, linkedin: true },
    video_viewer: { meta: true, google: true, tiktok: true, linkedin: false },
    custom: { meta: true, google: true, tiktok: true, linkedin: true }
};

const PLATFORM_LABELS = {
    meta: 'Meta',
    google: 'Google',
    tiktok: 'TikTok',
    linkedin: 'LinkedIn'
};

const PLATFORM_COLORS = {
    meta: '#1877F2',
    google: '#4285F4',
    tiktok: '#000000',
    linkedin: '#0A66C2'
};

/**
 * RetargetingPoolNode - Visual node for auto-generated retargeting audiences
 * Shows pool type, lookback window, and audience flow
 */
const RetargetingPoolNode = memo(({ data, selected }) => {
    const {
        name = 'New Retargeting Pool',
        poolType = 'engagement',
        sourceStage = 'TOFU',
        targetStage = 'MOFU',
        trigger = { type: 'engagement', threshold: 1, lookbackDays: 30 },
        estimatedSize = 0,
        isActive = true,
        status = 'draft',
        onEdit,
        onMenuClick
    } = data;

    const PoolIcon = POOL_TYPE_ICONS[poolType] || RefreshCw;

    const stageColors = {
        TOFU: 'var(--color-info)',
        MOFU: 'var(--color-warning)',
        BOFU: 'var(--color-success)'
    };

    const statusColors = {
        draft: 'var(--color-text-tertiary)',
        active: 'var(--color-success)',
        paused: 'var(--color-warning)',
        syncing: 'var(--color-info)'
    };

    const formatSize = (size) => {
        if (size >= 1000000) return `${(size / 1000000).toFixed(1)}M`;
        if (size >= 1000) return `${(size / 1000).toFixed(1)}K`;
        return size.toString();
    };

    return (
        <div
            style={{
                background: 'var(--color-bg-primary)',
                border: selected
                    ? '2px solid var(--color-warning)'
                    : '1px solid var(--color-border)',
                borderRadius: 'var(--radius-xl)',
                padding: 'var(--space-4)',
                minWidth: '200px',
                boxShadow: selected ? 'var(--shadow-lg)' : 'var(--shadow-md)',
                transition: 'all var(--transition-fast)',
                position: 'relative'
            }}
        >
            {/* Animated flow indicator */}
            {isActive && (
                <div style={{
                    position: 'absolute',
                    top: '-4px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '8px',
                    height: '8px',
                    background: 'var(--color-warning)',
                    borderRadius: '50%',
                    animation: 'pulse 2s infinite'
                }} />
            )}

            {/* Target handle (top) - receives from campaigns */}
            <Handle
                type="target"
                position={Position.Top}
                id="pool-in"
                style={{
                    background: stageColors[sourceStage],
                    width: '10px',
                    height: '10px'
                }}
            />

            {/* Header */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 'var(--space-3)'
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)'
                }}>
                    <div style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        background: 'var(--color-warning-light, rgba(245, 158, 11, 0.1))',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative'
                    }}>
                        <PoolIcon size={18} style={{ color: 'var(--color-warning)' }} />
                        <RefreshCw
                            size={10}
                            style={{
                                position: 'absolute',
                                bottom: '-2px',
                                right: '-2px',
                                color: 'var(--color-warning)',
                                background: 'var(--color-bg-primary)',
                                borderRadius: '50%',
                                padding: '1px'
                            }}
                        />
                    </div>
                    <div>
                        <div style={{
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--color-warning)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            fontWeight: 600
                        }}>
                            Retargeting Pool
                        </div>
                        <div style={{
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: 600,
                            color: 'var(--color-text-primary)',
                            maxWidth: '120px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}>
                            {name}
                        </div>
                    </div>
                </div>

                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onMenuClick?.();
                    }}
                    style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 'var(--space-1)',
                        borderRadius: 'var(--radius-sm)',
                        color: 'var(--color-text-tertiary)'
                    }}
                >
                    <MoreVertical size={16} />
                </button>
            </div>

            {/* Pool Type Badge */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                marginBottom: 'var(--space-3)'
            }}>
                <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 'var(--space-1)',
                    padding: '2px 8px',
                    background: 'var(--color-bg-tertiary)',
                    borderRadius: 'var(--radius-full)',
                    fontSize: 'var(--font-size-xs)'
                }}>
                    <PoolIcon size={10} />
                    {POOL_TYPE_LABELS[poolType]}
                </span>

                <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 'var(--space-1)',
                    padding: '2px 8px',
                    background: isActive ? 'rgba(34, 197, 94, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                    borderRadius: 'var(--radius-full)',
                    fontSize: 'var(--font-size-xs)'
                }}>
                    {isActive ? (
                        <Play size={10} fill="var(--color-success)" color="var(--color-success)" />
                    ) : (
                        <Pause size={10} fill="var(--color-warning)" color="var(--color-warning)" />
                    )}
                    <span style={{
                        color: isActive ? 'var(--color-success)' : 'var(--color-warning)',
                        textTransform: 'capitalize'
                    }}>
                        {isActive ? 'Active' : 'Paused'}
                    </span>
                </span>
            </div>

            {/* Flow Indicator */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 'var(--space-2)',
                padding: 'var(--space-2)',
                background: 'var(--color-bg-secondary)',
                borderRadius: 'var(--radius-md)',
                marginBottom: 'var(--space-2)'
            }}>
                <span style={{
                    padding: '2px 6px',
                    background: `${stageColors[sourceStage]}20`,
                    color: stageColors[sourceStage],
                    borderRadius: 'var(--radius-sm)',
                    fontSize: '10px',
                    fontWeight: 600
                }}>
                    {sourceStage}
                </span>
                <span style={{
                    color: 'var(--color-text-tertiary)',
                    fontSize: 'var(--font-size-xs)'
                }}>
                    →
                </span>
                <span style={{
                    padding: '2px 6px',
                    background: `${stageColors[targetStage]}20`,
                    color: stageColors[targetStage],
                    borderRadius: 'var(--radius-sm)',
                    fontSize: '10px',
                    fontWeight: 600
                }}>
                    {targetStage}
                </span>
            </div>

            {/* Stats */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-tertiary)'
            }}>
                <span>{trigger.lookbackDays} day lookback</span>
                <span style={{
                    fontWeight: 600,
                    color: 'var(--color-text-primary)'
                }}>
                    {formatSize(estimatedSize)} users
                </span>
            </div>

            {/* Platform Compatibility Indicators */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-1)',
                marginTop: 'var(--space-2)',
                paddingTop: 'var(--space-2)',
                borderTop: '1px dashed var(--color-border)'
            }}>
                <span style={{
                    fontSize: '9px',
                    color: 'var(--color-text-tertiary)',
                    marginRight: 'var(--space-1)'
                }}>
                    Platforms:
                </span>
                {Object.entries(PLATFORM_COMPATIBILITY[poolType] || {}).map(([platform, supported]) => (
                    <div
                        key={platform}
                        title={`${PLATFORM_LABELS[platform]}: ${supported ? 'Supported' : 'Not supported'}`}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '20px',
                            height: '20px',
                            borderRadius: 'var(--radius-sm)',
                            background: supported ? `${PLATFORM_COLORS[platform]}15` : 'var(--color-bg-tertiary)',
                            border: `1px solid ${supported ? PLATFORM_COLORS[platform] : 'var(--color-border)'}`,
                            opacity: supported ? 1 : 0.4
                        }}
                    >
                        {supported ? (
                            <Check size={10} style={{ color: PLATFORM_COLORS[platform] }} />
                        ) : (
                            <X size={10} style={{ color: 'var(--color-text-tertiary)' }} />
                        )}
                    </div>
                ))}
            </div>

            {/* Source handle (bottom) - feeds segments or ad sets */}
            <Handle
                type="source"
                position={Position.Bottom}
                id="pool-out"
                style={{
                    background: stageColors[targetStage],
                    width: '10px',
                    height: '10px'
                }}
            />

            <style>{`
                @keyframes pulse {
                    0%, 100% { opacity: 1; transform: translateX(-50%) scale(1); }
                    50% { opacity: 0.5; transform: translateX(-50%) scale(1.2); }
                }
            `}</style>
        </div>
    );
});

RetargetingPoolNode.displayName = 'RetargetingPoolNode';

export default RetargetingPoolNode;
